import { useMemo } from 'react';
import { FieldConfig } from '../../../../services/ValidationService';

/**
 * Hook for performance optimizations including expensive computations and memoizations.
 *
 * Provides memoized versions of expensive operations to prevent unnecessary recalculations
 * during re-renders, particularly for field grouping and processing operations.
 *
 * @param fieldsToRender - Array of field configurations to process
 * @returns Memoized expensive computations
 */

interface PerformanceOptimizations {
    groupInlineFields: (fields: FieldConfig[]) => Array<FieldConfig | FieldConfig[]>;
    memoizedFieldGroups: Array<FieldConfig | FieldConfig[]>;
}

export const usePerformanceOptimization = (fieldsToRender: FieldConfig[]): PerformanceOptimizations => {
    // Memoize the expensive groupInlineFields function
    const groupInlineFields = useMemo(() => {
        return (fields: FieldConfig[]) => {
            const groups: Array<FieldConfig | FieldConfig[]> = [];
            const processedFields = new Set<string>();

            fields.forEach(fieldConfig => {
                // Skip if already processed as part of an inline group
                if (processedFields.has(fieldConfig.name)) return;

                // Check if this field has inline layout
                if (fieldConfig.layout?.inline) {
                    const companionName = fieldConfig.layout.companion;

                    // Find the companion field only if companionName exists
                    if (companionName) {
                        const companionField = fields.find(f => f.name === companionName);

                        if (companionField && companionField.layout?.inline) {
                            // Group the fields based on position
                            const leftField = fieldConfig.layout.position === 'left' ? fieldConfig : companionField;
                            const rightField = fieldConfig.layout.position === 'right' ? fieldConfig : companionField;

                            groups.push([leftField, rightField]);
                            processedFields.add(fieldConfig.name);
                            processedFields.add(companionName);
                        } else {
                            // If companion not found or not inline, render as single field
                            groups.push(fieldConfig);
                            processedFields.add(fieldConfig.name);
                        }
                    } else {
                        // No companion specified, render as single field
                        groups.push(fieldConfig);
                        processedFields.add(fieldConfig.name);
                    }
                } else {
                    // Regular field
                    groups.push(fieldConfig);
                    processedFields.add(fieldConfig.name);
                }
            });

            return groups;
        };
    }, []); // Function is stable, no dependencies needed

    // Memoize the actual field groups calculation
    const memoizedFieldGroups = useMemo(() => {
        return groupInlineFields(fieldsToRender);
    }, [fieldsToRender, groupInlineFields]);

    return {
        groupInlineFields,
        memoizedFieldGroups,
    };
};
